import { AuthInput, Button } from "@cynthai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { isApiError } from "@/core/lib/api-errors";
import { addToast } from "@/core/lib/toast";
import type { ForgotPasswordSchema } from "@/features/auth/schemas/forgotPassword";
import { forgotPasswordSchema } from "@/features/auth/schemas/forgotPassword";
import { jwtForgotPasswordMutation } from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

function ForgotPassword() {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const [linkSent, setLinkSent] = useState(false);

  const resetPassword = useMutation({
    ...jwtForgotPasswordMutation(),
    onSuccess: () => {
      setLinkSent(true);
    },
    onError: (err) => {
      if (isApiError(err)) {
        addToast({
          title: m.toast_unknown_server_error(),
          severity: "danger",
        });
      }
    },
  });

  return (
    <>
      <p className="text-text-primary mb-2 text-2xl font-semibold">
        {linkSent ? m.email_sent() : m.forgot_password()}
      </p>

      <p className="text-text-secondary mb-10 max-w-75 text-sm font-light">
        {linkSent && resetPassword?.variables?.body?.email
          ? m.send_email_to_invite({
              email: resetPassword?.variables?.body?.email,
            })
          : m.enter_email_for_reset()}
      </p>

      {!linkSent && (
        <form
          onSubmit={handleSubmit((data) => {
            resetPassword.mutate({ body: { email: data.email } });
          })}
        >
          <AuthInput
            label={m.email()}
            type="email"
            placeholder={m.enter_email()}
            {...register("email")}
            errorMessage={errors.email?.message}
            isInvalid={errors.root?.message === undefined ? undefined : true}
          />

          <Button
            color="primary"
            className="mt-14 mb-4 w-full"
            type="submit"
            size="lg"
            isDisabled={resetPassword.isPending}
            isLoading={resetPassword.isPending}
          >
            {m.send_link_to_email()}
          </Button>
        </form>
      )}
    </>
  );
}

export const Route = createFileRoute("/auth/forgot-password")({
  component: () => <ForgotPassword />,
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }
  },
});
